import React from 'react';
import {
  PageHeader, KpiCard, SegmentedControl, FilterChip,
  Dialog, DialogContent, DialogHeader, DialogTitle,
  Button, ErrorState, useToast,
} from '@parsina/framework';
import { LedgerSkeleton } from './Credits.skeleton.jsx';
import { C, soft, GRADIENT_BRAND_DARK } from '../../constants/theme.js';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { type } from '../../constants/typography.js';
import { creditLevel, creditColor, CREDIT_LEVEL_META } from '../../constants/credit.js';
import { fmtCoins, coinsToTomanLabel } from '../../constants/creditPricing.js';
import { useCreditPrices } from '../../lib/useCreditPrices.js';
import { useCreditBalance, useCreditTransactions, useTopUp } from '../../hooks/useCredits.js';
import { CoinGlyph } from '../../components/common/CoinChip.jsx';
import TopUpSheet from './TopUpSheet.jsx';
import GiftCodeCard from './GiftCodeCard.jsx';
import LowBalanceCard from './LowBalanceCard.jsx';
import {
  PAGE_SIZE, TYPE_FILTERS, GROUP_MODES, DATE_PRESETS, rangeToISO,
  getServiceMeta, parseTransactionLabels, groupByDay, groupByType,
  computeStats, serviceBreakdown,
} from './Credits.shared.js';

// اعتبار — the desktop surface.
//
// ┌────────────────────────────────────────────────────────────────┐
// │ PageHeader «اعتبار پارسینا»                      [ خرید پارسه ] │
// ├────────────────────────────────────────────────────────────────┤
// │ ⚠ low/mid banner — only when the balance is actually low        │
// │ ┌──────────┬─────────────────────────────────────────────────┐ │
// │ │ hero     │ this month · per session · biggest consumer      │ │
// │ ├──────────┴──────────────────────────────┬──────────────────┤ │
// │ │ the ledger (filter · grouping · range)  │ gift code        │ │
// │ │                                          │ price list       │ │
// │ │                                          │ where it went    │ │
// │ │                                          │ low-balance      │ │
// └────────────────────────────────────────────────────────────────┘
//
// The body is FULL-BLEED, not a centred column. A `maxWidth` here leaves a band
// of empty background down one side of every wide screen — and in RTL a capped
// block sits against the right, so the cap reads as a layout bug rather than as
// a centred page. What gets capped is content INSIDE a card, never the body.

const CARD = {
  background: C.card,
  borderRadius: 16,
  border: `1px solid ${C.border}`,
  boxShadow: '0 1px 6px rgba(0,0,0,0.05)',
};

// ── Balance hero ────────────────────────────────────────────────────────────

function BalanceHero({ balance, onTopUp }) {
  const { t, formatNumber } = useTranslation();
  const { coinPriceToman } = useCreditPrices();
  const level = creditLevel(balance);
  const color = creditColor(balance);

  return (
    <div
      data-testid="credits-hero"
      style={{
        background: GRADIENT_BRAND_DARK,
        borderRadius: 20,
        padding: '22px 24px',
        color: '#fff',
        position: 'relative',
        overflow: 'hidden',
      }}
    >
      {/* Two soft discs, purely decorative — they give the gradient a centre of
          gravity so it does not read as a flat rectangle. */}
      <div style={{ position: 'absolute', top: -40, insetInlineStart: -40, width: 150, height: 150, borderRadius: '50%', background: 'rgba(255,255,255,0.05)' }} />
      <div style={{ position: 'absolute', bottom: -60, insetInlineEnd: -30, width: 190, height: 190, borderRadius: '50%', background: 'rgba(255,255,255,0.04)' }} />

      <div style={{ position: 'relative' }}>
        <div style={{ ...type.label, color: 'rgba(255,255,255,0.6)' }}>
          {t('credits.currentBalance')}
        </div>
        <div style={{ display: 'flex', alignItems: 'flex-end', gap: 8, marginTop: 6, flexWrap: 'wrap' }}>
          <span style={{ ...type.hero }} data-testid="credits-balance">
            {balance === null ? '—' : fmtCoins(balance, formatNumber)}
          </span>
          <span style={{ ...type.unit, color: 'rgba(255,255,255,0.7)', marginBottom: 6 }}>
            {t('credits.unit')}
          </span>
          {level && (
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, background: 'rgba(255,255,255,0.12)', borderRadius: 20, padding: '3px 10px', marginBottom: 5 }}>
              <span style={{ width: 8, height: 8, borderRadius: '50%', background: color }} />
              <span style={{ ...type.caption, color: 'rgba(255,255,255,0.85)' }}>
                {t(CREDIT_LEVEL_META[level].labelKey)}
              </span>
            </span>
          )}
        </div>
        {balance !== null && (
          <div style={{ ...type.caption, color: 'rgba(255,255,255,0.55)', marginTop: 8 }}>
            {coinsToTomanLabel(balance, { formatNumber, tomanLabel: t('credits.toman'), tomanPerCoin: coinPriceToman })}
          </div>
        )}
        <Button
          onClick={onTopUp}
          data-testid="credits-topup"
          variant="outline"
          style={{
            ...type.body,
            marginTop: 16,
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            background: 'rgba(255,255,255,0.16)',
            border: '1px solid rgba(255,255,255,0.2)',
            color: '#fff',
          }}
        >
          <CoinGlyph size={14} />
          {t('credits.buy')}
        </Button>
      </div>
    </div>
  );
}

// ── Rail: the price list ────────────────────────────────────────────────────

function PriceListCard() {
  const { t, formatNumber } = useTranslation();
  const [open, setOpen] = React.useState(false);
  // The policy's figures once the list has arrived; the shipped constants until
  // then — a price row never spins and never goes blank.
  const { priceList } = useCreditPrices();
  const rows = open ? priceList : priceList.slice(0, 5);

  return (
    <div style={{ ...CARD, padding: '14px 16px' }} data-testid="credits-pricelist">
      <div style={{ ...type.title, color: C.textPrimary, marginBottom: 4 }}>
        {t('credits.priceList.title')}
      </div>
      <div style={{ ...type.caption, color: C.textMuted, marginBottom: 10, lineHeight: 1.8 }}>
        {t('credits.priceList.hint')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        {rows.map((row, i) => (
          <div
            key={row.id}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              padding: '6px 0',
              borderBottom: i < rows.length - 1 ? `1px solid ${C.border}` : 'none',
            }}
          >
            <span style={{ ...type.label, flex: 1, color: C.textSecondary }}>{t(row.labelKey)}</span>
            <span style={{ ...type.label, fontWeight: 800, display: 'inline-flex', alignItems: 'center', gap: 4, color: C.teal, whiteSpace: 'nowrap' }}>
              <CoinGlyph size={11} />
              {fmtCoins(row.cost, formatNumber)}
              {row.perKey ? ` / ${t(row.perKey)}` : ''}
            </span>
          </div>
        ))}
      </div>
      {priceList.length > 5 && (
        <Button
          variant="ghost"
          size="sm"
          onClick={() => setOpen((o) => !o)}
          style={{ ...type.caption, fontWeight: 700, marginTop: 8, color: C.teal, paddingInline: 0 }}
        >
          {open ? t('common.actions.close') : t('credits.priceList.viewAll')}
        </Button>
      )}
    </div>
  );
}

// ── Rail: where the credit went ─────────────────────────────────────────────

function ServiceBreakdownCard({ transactions }) {
  const { t, formatNumber } = useTranslation();
  const rows = React.useMemo(() => serviceBreakdown(transactions, t), [transactions, t]);
  if (!rows.length) return null;
  const max = rows[0].amount || 1;

  return (
    <div style={{ ...CARD, padding: '14px 16px' }} data-testid="credits-breakdown">
      <div style={{ ...type.title, color: C.textPrimary, marginBottom: 12 }}>
        {t('credits.byService')}
      </div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
        {rows.map((row) => (
          <div key={row.key}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 5 }}>
              <span style={{ ...type.label, fontWeight: 700, flex: 1, color: C.textPrimary }}>
                {row.meta.label}
              </span>
              <span style={{ ...type.caption, color: C.textMuted }}>
                {t('credits.timesUsed', { count: formatNumber(row.count) })}
              </span>
              <span style={{ ...type.label, fontWeight: 800, color: row.meta.color }}>
                {fmtCoins(row.amount, formatNumber)}
              </span>
            </div>
            <div style={{ height: 5, background: C.bg, borderRadius: 4, overflow: 'hidden' }}>
              <div style={{
                height: '100%',
                width: `${(row.amount / max) * 100}%`,
                background: row.meta.color,
                borderRadius: 4,
                transition: 'width 0.6s cubic-bezier(.4,0,.2,1)',
              }}
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Ledger rows ─────────────────────────────────────────────────────────────

function TransactionRow({ txn }) {
  const { t, formatNumber, formatTime } = useTranslation();
  const meta = getServiceMeta(txn.serviceType, t);
  const Icon = meta.icon;
  const isDebit = txn.type === 'debit';
  const { title, sub } = parseTransactionLabels(txn.description, t);

  return (
    <div
      data-testid="credits-row"
      style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}
    >
      <div style={{ width: 34, height: 34, borderRadius: 9, background: meta.bg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <Icon size={16} color={meta.color} />
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ ...type.body, color: C.textPrimary }}>{title || meta.label}</div>
        <div style={{ ...type.micro, color: C.textMuted, marginTop: 2, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {formatTime(txn.createdAt)}
          {sub ? ` · ${sub}` : ''}
        </div>
      </div>
      {/* The sign is the whole story of a row, so it is never dropped: a debit
          reads «−۱۰» in ink and a credit «+۲۰۰۰» in green. */}
      <div style={{ ...type.amount, color: isDebit ? C.textSecondary : C.green, flexShrink: 0 }}>
        {isDebit ? '−' : '+'}
        {fmtCoins(txn.amount, formatNumber)}
      </div>
    </div>
  );
}

function TypeGroupHeader({ group }) {
  const { t, formatNumber } = useTranslation();
  const Icon = group.meta.icon;
  const spent = group.total >= 0;

  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 16px', background: C.bg }}>
      <span style={{ width: 22, height: 22, borderRadius: 6, background: group.meta.bg, display: 'grid', placeItems: 'center', flexShrink: 0 }}>
        <Icon size={13} color={group.meta.color} />
      </span>
      <span style={{ ...type.label, fontWeight: 800, flex: 1, color: C.textPrimary }}>
        {group.meta.label}
      </span>
      <span style={{ ...type.caption, color: C.textMuted }}>
        {t('credits.timesUsed', { count: formatNumber(group.items.length) })}
      </span>
      <span style={{ ...type.amount, color: spent ? C.textSecondary : C.green, minWidth: 52, textAlign: 'end' }}>
        {spent ? '−' : '+'}
        {fmtCoins(Math.abs(group.total), formatNumber)}
      </span>
    </div>
  );
}

// ── Main ────────────────────────────────────────────────────────────────────

const CreditsWeb = () => {
  const { t, formatNumber, formatDate } = useTranslation();
  const toast = useToast();

  const { balance, reload: reloadBalance } = useCreditBalance();
  const { startTopUp, busy: payBusy, error: payError } = useTopUp();

  const [page, setPage] = React.useState(0);
  const [typeFilter, setTypeFilter] = React.useState('all');
  const [groupMode, setGroupMode] = React.useState('day');
  const [datePreset, setDatePreset] = React.useState('all');
  const [topUpOpen, setTopUpOpen] = React.useState(false);

  const { startDate, endDate } = React.useMemo(() => rangeToISO(datePreset), [datePreset]);

  const {
    transactions, totalCount, loading, error, reload,
  } = useCreditTransactions({
    limit: PAGE_SIZE,
    offset: page * PAGE_SIZE,
    startDate,
    endDate,
    // Server-side. The ledger runs to thousands of rows, so filtering the
    // fetched page would show only the matches that happened to land on it,
    // under a count describing the whole set.
    type: typeFilter === 'all' ? '' : typeFilter,
  });

  // ── The gateway return leg ──
  //
  // By the time the browser lands here the payment BFF has ALREADY verified and
  // settled with the bank — it is the only party that can, because the gateway
  // reports server-to-server and Sepehr's settle step needs a receipt the
  // browser never sees. So this screen reports the outcome and refreshes; it
  // must not try to confirm anything itself.
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const settled = params.get('payment');
    if (!settled) return;
    // Stripped immediately, so a refresh cannot replay the message.
    window.history.replaceState({}, '', window.location.pathname);
    if (settled === 'success') toast.success(t('credits.payment.done'));
    else toast.error(t('credits.payment.failed'));
    reloadBalance();
    reload();
    // `t` is stable per locale and the toast/reload identities are stable; this
    // must run once per landing, not on every render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const stats = React.useMemo(() => computeStats(transactions), [transactions]);
  const dayGroups = React.useMemo(
    () => groupByDay(transactions, t, formatDate),
    [transactions, t, formatDate],
  );
  const typeGroups = React.useMemo(() => groupByType(transactions, t), [transactions, t]);
  const totalPages = Math.max(1, Math.ceil(totalCount / PAGE_SIZE));

  // Changing the server-side window must reset paging, or the offset points
  // past the end of the new set and the ledger renders empty.
  const applyDatePreset = (value) => { setDatePreset(value); setPage(0); };

  const level = creditLevel(balance);
  const topMeta = stats.topService ? getServiceMeta(stats.topService, t) : null;
  const filtered = typeFilter !== 'all' || datePreset !== 'all';

  // A NON-BREAKING SPACE, and it is load-bearing. The kit's KpiCard separates
  // its value from its unit with `marginRight: 4` — which is the LEADING edge in
  // RTL and the trailing one in LTR, so under `en` the tiles rendered
  // «46Parseh» and «Clinical assistant87%» with no gap at all. The kit is a
  // pinned git dependency shared with two other apps, so the gap is supplied
  // here rather than by forking it; remove this when KpiCard uses a logical
  // margin.
  const unit = (label) => `\u00A0${label}`;

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: C.bg, overflowY: 'auto' }}>
      <PageHeader title={t('credits.title')} sub={t('credits.subtitle')}>
        <Button onClick={() => setTopUpOpen(true)} data-testid="credits-topup-header">
          <CoinGlyph size={14} />
          <span style={{ marginInlineStart: 6 }}>{t('credits.buy')}</span>
        </Button>
      </PageHeader>

      <div style={{ padding: '20px 28px', display: 'flex', flexDirection: 'column', gap: 18, width: '100%', boxSizing: 'border-box' }}>

        {/* Alert zone — low and mid only. An «ok» banner would be noise on
            every visit, and a banner that is always there is not a banner. */}
        {(level === 'low' || level === 'mid') && (
          <div
            data-testid="credits-low-banner"
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              padding: '10px 16px',
              borderRadius: 10,
              background: CREDIT_LEVEL_META[level].tint,
              border: `1px solid ${soft(CREDIT_LEVEL_META[level].color, 0.35)}`,
            }}
          >
            <span style={{ ...type.body, color: CREDIT_LEVEL_META[level].color, flex: 1 }}>
              {t(level === 'low' ? 'credits.banner.low' : 'credits.banner.mid', {
                coins: fmtCoins(balance, formatNumber),
              })}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setTopUpOpen(true)}
              style={{ borderColor: CREDIT_LEVEL_META[level].color, color: CREDIT_LEVEL_META[level].color }}
            >
              {t('credits.topUp')}
            </Button>
          </div>
        )}

        {/* Hero + KPI strip */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(260px, 300px) 1fr', gap: 14, alignItems: 'stretch' }}>
          <BalanceHero balance={balance} onTopUp={() => setTopUpOpen(true)} />
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 12 }}>
            <KpiCard
              label={t('credits.kpi.month')}
              value={fmtCoins(stats.usedThisMonth, formatNumber)}
              unit={unit(t('credits.unit'))}
              loading={loading && !transactions.length}
              icon={<span style={{ color: C.teal, display: 'flex' }}><CoinGlyph size={18} /></span>}
              bg={soft(C.teal, 0.12)}
              accent={C.textPrimary}
            />
            <KpiCard
              label={t('credits.kpi.perVisit')}
              value={stats.avgPerVisit == null ? '—' : fmtCoins(stats.avgPerVisit, formatNumber)}
              unit={unit(t('credits.unit'))}
              loading={loading && !transactions.length}
              icon={<span style={{ color: C.navy, display: 'flex' }}><CoinGlyph size={18} /></span>}
              bg={soft(C.navy, 0.1)}
              accent={C.textPrimary}
            />
            <KpiCard
              label={t('credits.kpi.topService')}
              value={topMeta ? topMeta.label : '—'}
              unit={topMeta ? unit(t('credits.percent', { value: formatNumber(stats.topPct) })) : ''}
              loading={loading && !transactions.length}
              icon={topMeta ? <topMeta.icon size={18} color={topMeta.color} /> : undefined}
              bg={topMeta ? topMeta.bg : C.bg}
              accent={C.textPrimary}
            />
          </div>
        </div>

        {/* Ledger + rail */}
        <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1fr) 280px', gap: 14, alignItems: 'start' }}>

          {/* The ledger reports its region state like every other fetching
              region (DESIGN.md §"Loading & skeleton", three states): loading
              skeletons, empty and error are never confused. */}
          <div
            style={{ ...CARD, overflow: 'hidden' }}
            data-testid="credits-ledger"
            data-state={
              transactions.length ? 'content'
                : error && !loading ? 'error'
                  : loading ? 'loading'
                    : 'empty'
            }
            aria-busy={loading && !transactions.length ? true : undefined}
          >
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, padding: '14px 16px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ ...type.title, color: C.textPrimary }}>
                {t('credits.transactions')}
                {totalCount > 0 && (
                  <span style={{ ...type.caption, color: C.textMuted, marginInlineStart: 8 }}>
                    ({t('credits.itemCount', { count: formatNumber(totalCount) })})
                  </span>
                )}
              </div>
              <SegmentedControl
                options={TYPE_FILTERS(t)}
                value={typeFilter}
                onChange={(v) => { setTypeFilter(v); setPage(0); }}
              />
            </div>

            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', padding: '10px 16px', borderBottom: `1px solid ${C.border}` }}>
              <span style={{ ...type.caption, color: C.textMuted }}>{t('credits.group.label')}</span>
              <SegmentedControl
                options={GROUP_MODES(t)}
                value={groupMode}
                onChange={setGroupMode}
              />
              <div
                data-testid="credits-daterange"
                role="group"
                aria-label={t('credits.range.label')}
                style={{ marginInlineStart: 'auto', display: 'flex', gap: 6, flexWrap: 'wrap' }}
              >
                {DATE_PRESETS(t).map((p) => (
                  <FilterChip
                    key={p.value}
                    label={p.label}
                    active={datePreset === p.value}
                    onClick={() => applyDatePreset(p.value)}
                    className="min-h-9"
                  />
                ))}
              </div>
            </div>

            {error && !loading && transactions.length === 0 && (
              <div role="alert" style={{ padding: 16 }}>
                <ErrorState
                  title={t('credits.error.title')}
                  description={t('credits.error.body')}
                  onRetry={reload}
                  retryLabel={t('common.actions.retry')}
                />
              </div>
            )}

            {/* Only the ledger body skeletons — in TransactionRow's own shape —
                while the header, the filters and the KPI tiles above stay real
                (DESIGN.md §"Loading & skeleton", region load). */}
            {loading && transactions.length === 0 && (
              <LedgerSkeleton label={t('common.loading')} />
            )}

            {!loading && !error && transactions.length === 0 && (
              <div style={{ padding: '44px 20px', textAlign: 'center' }} data-testid="credits-empty">
                <span style={{ color: C.textMuted, display: 'inline-flex' }}><CoinGlyph size={34} /></span>
                <div style={{ ...type.body, color: C.textPrimary, margin: '10px 0 4px' }}>
                  {filtered ? t('credits.empty.filtered') : t('credits.empty.title')}
                </div>
                <div style={{ ...type.label, color: C.textMuted }}>
                  {filtered ? t('credits.empty.filteredHint') : t('credits.empty.hint')}
                </div>
              </div>
            )}

            {!loading && groupMode === 'day' && dayGroups.map((group) => (
              <div key={group.key}>
                <div style={{ ...type.caption, fontWeight: 700, padding: '6px 16px', color: C.textMuted, background: C.bg }}>
                  {group.label}
                </div>
                {group.items.map((txn) => <TransactionRow key={txn.id} txn={txn} />)}
              </div>
            ))}

            {!loading && groupMode === 'type' && typeGroups.map((group) => (
              <div key={group.key}>
                <TypeGroupHeader group={group} />
                {group.items.map((txn) => <TransactionRow key={txn.id} txn={txn} />)}
              </div>
            ))}

            {/* The window is stated even on a single page: without it a ledger
                that fits on one page looks unpaged, and one that does not gives
                no sense of scale. */}
            {totalCount > 0 && !loading && (
              <div
                data-testid="credits-range"
                style={{ padding: '10px 16px 0', textAlign: 'center', ...type.caption, color: C.textMuted }}
              >
                {t('credits.showingRange', {
                  from: formatNumber(page * PAGE_SIZE + 1),
                  to: formatNumber(Math.min((page + 1) * PAGE_SIZE, totalCount)),
                  total: formatNumber(totalCount),
                })}
              </div>
            )}

            {/* A PAGER, deliberately not «نمایش بیشتر». The hook REPLACES its
                page rather than appending, so a grow button would silently
                discard the rows already on screen and offer no way back to
                them — and in a ledger of thousands, stepping forward one page
                at a time with no way back is not a way to find anything. */}
            {totalPages > 1 && !loading && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '12px 16px', borderTop: `1px solid ${C.border}` }}>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => Math.max(0, p - 1))}
                  disabled={page === 0}
                  data-testid="credits-prev"
                >
                  {t('credits.pager.previous')}
                </Button>
                <span style={{ ...type.label, color: C.textSecondary, whiteSpace: 'nowrap' }} data-testid="credits-page-label">
                  {t('credits.pager.page', {
                    page: formatNumber(page + 1),
                    total: formatNumber(totalPages),
                  })}
                </span>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage((p) => (p + 1 < totalPages ? p + 1 : p))}
                  disabled={page + 1 >= totalPages}
                  data-testid="credits-next"
                >
                  {t('credits.pager.next')}
                </Button>
              </div>
            )}
          </div>

          {/* Rail */}
          <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
            {/* Above the price list on purpose: a dentist who arrived from a
                campaign SMS has a code in hand and nothing else to do here. */}
            <GiftCodeCard onRedeemed={() => { reloadBalance(); reload(); }} />
            <PriceListCard />
            <ServiceBreakdownCard transactions={transactions} />
            <LowBalanceCard />
          </div>
        </div>
      </div>

      <Dialog open={topUpOpen} onOpenChange={setTopUpOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t('credits.buy')}</DialogTitle></DialogHeader>
          <TopUpSheet
            avgPerVisit={stats.avgPerVisit}
            onPay={startTopUp}
            busy={payBusy}
            error={payError}
          />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default CreditsWeb;
